import axios, {AxiosInstance} from 'axios';
import {camelizeKeys} from 'humps';

import {tasksService} from 'api/tasks';
import {apiAxios} from 'IocContainer';
import {firebaseService} from 'services/Firebase/Firebase';
import {AppConstantsResponse} from 'shared/hooks/useAppConstants';
import {
  MatrixConnectionToken,
  MatrixSessionBody,
  MatrixSessionResponse,
  Unread,
  UnreadInfo,
  MatrixApiError,
} from 'shared/models/task/chat';

import {camelCaseConverter} from './axiosInterceptors';

function isMatrixApiError(error: unknown): error is MatrixApiError {
  return typeof error === 'object' && error !== null && 'response' in error;
}

export class MatrixService {
  private static instance: MatrixService;
  private client: AxiosInstance | null = null;
  private appConstants: AppConstantsResponse | null = null;
  private readonly mediaEndpoint = '/_matrix/media/v3';
  private readonly clientEndpoint = '/_matrix/client/v3';

  // Token expiration management
  private tokenExpirationTime: number | null = null;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly TOKEN_REFRESH_BUFFER_MS = 60000; // Refresh 1 min before expiry

  private session: {
    accessToken: string;
    refreshToken: string | null;
    workerId: string;
  } | null = null;

  private initializationPromise: Promise<MatrixSessionResponse> | null = null;
  private isInitializing = false;

  public static getInstance(): MatrixService {
    if (!MatrixService.instance) {
      MatrixService.instance = new MatrixService();
    }
    return MatrixService.instance;
  }

  private async getMatrixConnectionToken(workerId: string) {
    const res = await apiAxios.get<MatrixConnectionToken>(`/workers/${workerId}/chatauth`);
    return res.data;
  }

  private async ensureAppConstants() {
    if (!this.appConstants) {
      this.appConstants = await tasksService.getAppConstants();
      if (!this.appConstants) {
        throw new Error('Failed to fetch configuration');
      }
    }
    return this.appConstants;
  }

  private async forceRelogin(): Promise<void> {
    if (!this.session?.workerId) {
      const workerId = firebaseService.getWorkerIdFromLS();
      if (workerId) {
        await this.login(workerId);
      } else {
        throw new Error('No worker ID available for re-login');
      }
    } else {
      await this.login(this.session.workerId);
    }
  }

  private async refreshToken(): Promise<void> {
    if (!this.client || !this.session?.refreshToken) {
      throw new Error('Cannot refresh token - no refresh token available');
    }

    try {
      const response = await this.client.post<MatrixSessionResponse>(
        `${this.clientEndpoint}/refresh`,
        {
          refresh_token: this.session.refreshToken,
        },
        {
          disableCamelCaseConverter: true,
        },
      );

      this.session = {
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
        workerId: this.session.workerId,
      };

      // use matrix spec expires_in_ms field
      if (response.data.expires_in_ms) {
        this.tokenExpirationTime = Date.now() + response.data.expires_in_ms;
      }

      this.updateAuthHeader();
    } catch (error: unknown) {
      if (isMatrixApiError(error)) {
        // if refresh token is invalid, clear session and re-login
        if (error.response?.status === 401) {
          this.cleanup();
          throw new Error('Refresh token expired - re-login required');
        }

        if (error.response?.data?.errcode === 'M_UNKNOWN_TOKEN') {
          this.cleanup();
          throw new Error('Unknown refresh token - re-login required');
        }

        if (error.response?.data?.soft_logout) {
          await this.login(this.session.workerId);
          return;
        }
      }

      this.cleanup();
      throw error;
    }
  }

  private async ensureValidToken(): Promise<void> {
    if (!this.session?.refreshToken || !this.tokenExpirationTime) return;

    const now = Date.now();
    const shouldRefresh = now + this.TOKEN_REFRESH_BUFFER_MS >= this.tokenExpirationTime;

    if (shouldRefresh) {
      await this.refreshToken();
    }
  }

  private updateAuthHeader(): void {
    if (!this.client || !this.session?.accessToken) return;
    this.client.defaults.headers.common['Authorization'] = `Bearer ${this.session.accessToken}`;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private async _doLogin(workerId: string): Promise<MatrixSessionResponse> {
    try {
      const constants = await this.ensureAppConstants();

      this.client = axios.create({
        baseURL: constants.locales.uS.matrix.homeserver,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const credentials = await this.getMatrixConnectionToken(workerId);

      const body: MatrixSessionBody = {
        type: 'm.login.password',
        identifier: {
          type: 'm.id.user',
          user: credentials.id,
        },
        password: credentials.password,
        refresh_token: true,
      };

      const response = await this.client.post<MatrixSessionResponse>(`${this.clientEndpoint}/login`, body);

      if (!response?.data?.access_token) {
        throw new Error('Invalid Matrix login response');
      }

      this.session = {
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token || null,
        workerId,
      };

      // set token expiration using matrix spec expires_in_ms
      if (response.data.expires_in_ms) {
        this.tokenExpirationTime = Date.now() + response.data.expires_in_ms;
      }

      this.setupInterceptors();

      return response.data;
    } catch (error) {
      this.cleanup();
      throw error;
    }
  }

  private setupInterceptors(): void {
    if (!this.client) return;

    this.client.interceptors.response.use(
      (response) => {
        if (response.config.disableCamelCaseConverter) {
          return response;
        }

        if (response.data && response.headers['content-type'] === 'application/json') {
          response.data = camelizeKeys(response.data, camelCaseConverter);
        }
        return response;
      },
      async (error: unknown) => {
        if (!isMatrixApiError(error)) {
          return Promise.reject(error);
        }

        // Handle Matrix-specific error codes
        if (error.response?.status === 401 && !error.config?._retry) {
          const errcode = error.response?.data?.errcode;

          if ((errcode === 'M_UNKNOWN_TOKEN' || !errcode) && this.session?.refreshToken) {
            if (error.config) {
              error.config._retry = true;
            }

            try {
              await this.refreshToken();
              if (this.client && error.config) {
                return this.client.request(error.config);
              }
            } catch (refreshError) {
              try {
                await this.forceRelogin();
                if (this.client && error.config) {
                  return this.client.request(error.config);
                }
              } catch (reloginError) {
                throw reloginError;
              }
            }
          }
        }

        return Promise.reject(error);
      },
    );

    this.updateAuthHeader();
  }

  private cleanup(): void {
    this.client = null;
    this.session = null;
    this.tokenExpirationTime = null;
  }

  public async login(workerId: string): Promise<MatrixSessionResponse> {
    if (this.isInitializing && this.initializationPromise) {
      return this.initializationPromise;
    }

    try {
      this.isInitializing = true;
      this.initializationPromise = this._doLogin(workerId);
      return await this.initializationPromise;
    } finally {
      this.isInitializing = false;
      this.initializationPromise = null;
    }
  }

  public logout(): void {
    this.cleanup();
  }

  public isInitialized(): boolean {
    return !!(this.client && this.session?.accessToken);
  }

  public getClient(): AxiosInstance {
    if (!this.isInitialized()) {
      const workerId = firebaseService.getWorkerIdFromLS();
      if (workerId) {
        throw new Error('Matrix client needs reinitialization');
      }
      throw new Error('Matrix client not initialized and no worker ID available');
    }

    if (!this.client) {
      throw new Error('Client is not available');
    }

    return this.client;
  }

  private async ensureAuthenticated() {
    if (!this.isInitialized()) {
      const workerId = firebaseService.getWorkerIdFromLS();
      if (workerId) {
        await this.login(workerId);
      } else {
        throw new Error('No worker ID available for authentication');
      }
    } else {
      // proactively refresh if token is about to expire
      await this.ensureValidToken();
    }
  }

  public async getServerConfig() {
    await this.ensureAuthenticated();
    const res = await this.getClient().get<{'m.upload.size': number}>(`${this.mediaEndpoint}/config`);
    return res.data;
  }

  public async getJoinedRooms() {
    await this.ensureAuthenticated();
    const res = await this.getClient().get<{joinedRooms: string[]}>(`${this.clientEndpoint}/joined_rooms`);
    return res.data.joinedRooms;
  }

  public async uploadAsset(file: File) {
    await this.ensureAuthenticated();

    const extension = file.type.split('/').pop();
    const body = await file.arrayBuffer();

    const res = await this.getClient().post<{contentUri: string}>(
      `${this.mediaEndpoint}/upload?filename=${encodeURIComponent(file.name)}.${extension}`,
      body,
      {
        headers: {
          'Content-Type': file.type,
        },
      },
    );
    return res.data;
  }

  public async getUnreadCount(roomIds: string[]) {
    if (!roomIds?.length) return;

    await this.ensureAuthenticated();

    const unreadInfos: UnreadInfo[] = [];

    const syncFilter = {
      room: {
        timeline: {limit: 1},
        includeLeave: false,
      },
    };

    const syncResponse = await this.getClient().get(
      `${this.clientEndpoint}/sync?filter=${encodeURIComponent(JSON.stringify(syncFilter))}&timeout=0`,
    );

    for (const roomId of roomIds) {
      if (syncResponse.data.rooms?.join?.[roomId]) {
        const roomInfo = syncResponse.data.rooms.join[roomId];
        if (roomInfo.unreadNotifications) {
          unreadInfos.push({
            roomId,
            total: roomInfo.unreadNotifications.notificationCount || 0,
            highlight: roomInfo.unreadNotifications.highlightCount || 0,
          });
        }
      }
    }

    const unread: Unread = {
      total: 0,
      highlight: 0,
      from: new Set(),
    };

    unreadInfos.forEach((info) => {
      if (info.total > 0) {
        unread.total += info.total;
        unread.highlight += info.highlight;
        unread.from?.add(info.roomId);
      }
    });

    return unread;
  }
}

export const matrixService = MatrixService.getInstance();
